import { useCallback, useRef, useState } from 'react';
import { apiPost } from 'src/lib/api/axios';
import { toast } from 'src/components/ui/toast';

interface UseWhisperReturn {
  /** Whether the microphone is currently recording */
  isRecording: boolean;
  /** Whether we are currently processing/transcribing the recorded audio or initializing the stream */
  isProcessing: boolean;
  /** Latest transcript once `stopRecording` resolves */
  transcript: string | null;
  /** History of audio levels for waveform visualization */
  audioHistory: number[];
  /** Start capturing audio and transcribing */
  startRecording: () => Promise<void>;
  /** Stop recording and trigger transcription */
  stopRecording: () => Promise<void>;
  /** Cancel recording without transcription */
  cancelRecording: () => Promise<void>;
}

const getStream = async () => {
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    console.error('Could not initialize audio stream', err);
    throw err;
  }
};

export default function useWhisper(
  silenceThreshold: number = 0.1,
  minRecordingDuration: number = 1000
): UseWhisperReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [audioHistory, setAudioHistory] = useState<number[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastHistoryUpdateRef = useRef<number>(0);
  const recordingStartTimeRef = useRef<number>(0);
  const lastSoundTimeRef = useRef<number>(0);
  const stopRecordingRef = useRef<(() => Promise<void>) | null>(null);
  const audioHistoryRef = useRef<number[]>([]);
  const wasCancelledRef = useRef<boolean>(false);

  const stopRecording = useCallback(async () => {
    if (!isRecording || !mediaRecorderRef.current) return;

    // Stop audio analysis
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setAudioHistory([]);
    lastHistoryUpdateRef.current = 0;
    recordingStartTimeRef.current = 0;
    lastSoundTimeRef.current = 0;

    mediaRecorderRef.current.stop();
    // Stop all tracks to remove browser recording indicator
    mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, [isRecording]);

  const cancelRecording = useCallback(async () => {
    if (!isRecording || !mediaRecorderRef.current) return;

    // Mark as cancelled to prevent processing
    wasCancelledRef.current = true;

    // Stop audio analysis
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setAudioHistory([]);
    lastHistoryUpdateRef.current = 0;
    recordingStartTimeRef.current = 0;
    lastSoundTimeRef.current = 0;

    // Clear audio chunks to prevent transcription
    audioChunksRef.current = [];
    audioHistoryRef.current = [];

    mediaRecorderRef.current.stop();
    // Stop all tracks to remove browser recording indicator
    mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    mediaRecorderRef.current = null;
    setIsRecording(false);
  }, [isRecording]);

  // Keep ref in sync with the current stopRecording function
  stopRecordingRef.current = stopRecording;

  const startRecording = useCallback(async () => {
    if (isRecording) return;

    try {
      // Show loading spinner because the first time we request the stream, it takes a while to get permission
      setIsProcessing(true);
      const stream = await getStream();
      setIsProcessing(false);

      // Set up audio analysis for waveform visualization
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          // Skip processing if recording was explicitly cancelled
          if (wasCancelledRef.current) {
            wasCancelledRef.current = false; // Reset for next recording
            audioHistoryRef.current = []; // Clear after cancellation
            return;
          }

          setIsProcessing(true);

          // Check recording duration and audio quality
          const recordingDuration = performance.now() - recordingStartTimeRef.current;
          const hasSignificantAudio = audioHistoryRef.current.some((level) => level >= silenceThreshold);

          // Skip transcription if recording is too short (< 1 second) or has no significant audio
          if (recordingDuration < minRecordingDuration || !hasSignificantAudio) {
            setIsProcessing(false);
            toast.info('Recording too short or quiet. Please try again.');
            audioHistoryRef.current = []; // Clear after check is complete
            return;
          }

          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/ogg' });
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.ogg');

          const response = await apiPost('/helpers/transcribe-audio', formData, {
            meta: {
              globalError: false,
              context: 'voice-input'
            },
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            timeout: 20000
          });

          setTranscript(response.data.text);
        } catch (err) {
          console.error('Transcription failed', err);
          toast.error('Transcription failed. Please try again.');
        } finally {
          setIsProcessing(false);
          audioHistoryRef.current = []; // Clear after transcription is complete
        }
      };

      // Start audio level analysis
      const analyzeAudio = () => {
        if (!analyserRef.current) return;

        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average amplitude
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const normalizedLevel = average / 255; // Normalize to 0-1 range

        // Track silence for auto-stop functionality
        const now = performance.now();
        if (normalizedLevel >= silenceThreshold) {
          lastSoundTimeRef.current = now;
        }

        // Check for auto-stop due to silence
        const silenceDuration = now - lastSoundTimeRef.current;
        if (silenceDuration > 5000) {
          if (stopRecordingRef.current) {
            stopRecordingRef.current();
          }
          return; // Exit the animation loop
        }

        // Update audio history every 100ms (10fps) for slower scrolling
        if (now - lastHistoryUpdateRef.current > 100) {
          setAudioHistory((prev) => {
            const newHistory = [...prev, normalizedLevel];
            const slicedHistory = newHistory.slice(-30); // Keep last 30 samples (3 seconds of audio at 10fps)
            return slicedHistory;
          });
          // Keep complete history in ref for transcription quality check
          audioHistoryRef.current = [...audioHistoryRef.current, normalizedLevel];
          lastHistoryUpdateRef.current = now;
        }

        // Continue animation loop - we'll stop it explicitly in stopRecording
        animationFrameRef.current = requestAnimationFrame(analyzeAudio);
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Reset cancellation flag for new recording
      wasCancelledRef.current = false;

      // Initialize timing references
      const startTime = performance.now();
      recordingStartTimeRef.current = startTime;
      lastSoundTimeRef.current = startTime;
      lastHistoryUpdateRef.current = startTime;

      // Start audio analysis
      analyzeAudio();
    } catch (err) {
      console.error('Could not start audio recording', err);
      toast.error('Could not start audio recording. Please try again.');
      setIsProcessing(false);
    }
  }, [isRecording, isProcessing, stopRecording]);

  return {
    isRecording,
    isProcessing,
    transcript,
    audioHistory,
    startRecording,
    stopRecording,
    cancelRecording
  };
}
